import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { BulkAssignRfpQuestionsRequest, RfpAssignableUser } from "../../types/rfp";
import { Drawer } from "./Drawer";

type AssignScope = "rfp" | "section" | "custom";

export type AssignSectionOption = {
  readonly name: string;
  readonly total: number;
};

export type AssignQuestionOption = {
  readonly id: string;
  readonly indexLabel: string;
  readonly sectionName: string;
  readonly text: string;
};

type ScopeOptionRowProps = {
  readonly value: AssignScope;
  readonly currentScope: AssignScope;
  readonly label: string;
  readonly onChange: (scope: AssignScope) => void;
};

const ScopeOptionRow = ({ value, currentScope, label, onChange }: ScopeOptionRowProps) => {
  const isSelected = value === currentScope;

  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded border px-3 py-3 transition-colors ${
        isSelected ? "border-primary-500 bg-sky-50" : "border-gray-200 bg-white"
      }`}
    >
      <input
        type="radio"
        name="assign-scope"
        value={value}
        checked={isSelected}
        onChange={() => onChange(value)}
        className="h-4 w-4 accent-primary-500"
      />
      <span className="text-sm font-medium text-gray-800">{label}</span>
    </label>
  );
};

type RfpAssignQuestionsDrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly sections: readonly AssignSectionOption[];
  readonly questions: readonly AssignQuestionOption[];
  readonly users: readonly RfpAssignableUser[];
  readonly isSubmitting: boolean;
  readonly errorMessage?: string | null;
  readonly onAssign: (payload: BulkAssignRfpQuestionsRequest) => Promise<boolean>;
};

export const RfpAssignQuestionsDrawer = ({
  isOpen,
  onClose,
  sections,
  questions,
  users,
  isSubmitting,
  errorMessage,
  onAssign,
}: RfpAssignQuestionsDrawerProps) => {
  const [scope, setScope] = useState<AssignScope>("rfp");
  const [selectedSectionName, setSelectedSectionName] = useState("");
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [questionSearch, setQuestionSearch] = useState("");
  const [assignedUserId, setAssignedUserId] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setScope("rfp");
    setSelectedSectionName("");
    setSelectedQuestionIds([]);
    setQuestionSearch("");
    setAssignedUserId("");
    setValidationError(null);
  }, [isOpen]);

  useEffect(() => {
    if (scope !== "section") {
      return;
    }
    if (selectedSectionName || sections.length === 0) {
      return;
    }
    setSelectedSectionName(sections[0].name);
  }, [scope, selectedSectionName, sections]);

  const filteredQuestions = useMemo(() => {
    const query = questionSearch.trim().toLowerCase();
    if (!query) return questions;
    return questions.filter(
      (question) =>
        question.text.toLowerCase().includes(query) ||
        question.sectionName.toLowerCase().includes(query),
    );
  }, [questionSearch, questions]);

  const handleToggleQuestion = (questionId: string) => {
    setSelectedQuestionIds((current) =>
      current.includes(questionId)
        ? current.filter((id) => id !== questionId)
        : [...current, questionId],
    );
  };

  const handleAssign = async () => {
    const normalizedAssignedUserId = assignedUserId.trim();
    const normalizedSectionName = selectedSectionName.trim();
    const normalizedQuestionIds = selectedQuestionIds.filter((id) => id.trim().length > 0);
    if (!normalizedAssignedUserId) {
      setValidationError("Please select a user to assign.");
      return;
    }

    let payload: BulkAssignRfpQuestionsRequest;
    if (scope === "rfp") {
      payload = {
        assigned_to: normalizedAssignedUserId,
        scope: "entire_rfp",
      };
    } else if (scope === "section") {
      if (!normalizedSectionName) {
        setValidationError("Please select a section.");
        return;
      }
      payload = {
        assigned_to: normalizedAssignedUserId,
        scope: "section",
        section_name: normalizedSectionName,
      };
    } else {
      if (normalizedQuestionIds.length === 0) {
        setValidationError("Please select at least one question.");
        return;
      }
      payload = {
        assigned_to: normalizedAssignedUserId,
        scope: "custom",
        question_ids: normalizedQuestionIds,
      };
    }

    setValidationError(null);
    await onAssign(payload);
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Questions"
      className="flex flex-1 flex-col p-0"
    >
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="mb-6">
          <p className="mb-3 text-sm font-semibold text-gray-800">Select Scope</p>
          <div className="space-y-2">
            <ScopeOptionRow
              value="rfp"
              currentScope={scope}
              label={`Entire RFP (${questions.length} questions)`}
              onChange={setScope}
            />
            <ScopeOptionRow
              value="section"
              currentScope={scope}
              label="Select Section"
              onChange={setScope}
            />
            <ScopeOptionRow
              value="custom"
              currentScope={scope}
              label="Custom (Across Sections)"
              onChange={setScope}
            />
          </div>
        </div>

        {scope === "section" ? (
          <div className="mb-6">
            <label
              htmlFor="assign-section"
              className="mb-2 block text-sm font-semibold text-gray-800"
            >
              Select Section
            </label>
            <select
              id="assign-section"
              value={selectedSectionName}
              onChange={(event) => setSelectedSectionName(event.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none transition-all focus:border-primary-500 focus:shadow-[0_0_0_3px_rgba(0,156,229,0.1)]"
            >
              <option value="">Select a section...</option>
              {sections.map((section) => (
                <option key={section.name} value={section.name}>
                  {section.name} ({section.total} questions)
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {scope === "custom" ? (
          <div className="mb-6">
            <label
              htmlFor="assign-question-search"
              className="mb-2 block text-sm font-semibold text-gray-800"
            >
              Select Questions
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                id="assign-question-search"
                type="text"
                value={questionSearch}
                onChange={(event) => setQuestionSearch(event.target.value)}
                placeholder="Search questions..."
                className="w-full rounded border border-gray-200 py-2 pl-9 pr-3 text-sm text-gray-800 outline-none transition-all focus:border-primary-500 focus:shadow-[0_0_0_3px_rgba(0,156,229,0.1)]"
              />
            </div>

            <div className="mt-3 max-h-80 overflow-y-auto rounded border border-gray-200">
              {filteredQuestions.length ? (
                filteredQuestions.map((question) => {
                  const isSelected = selectedQuestionIds.includes(question.id);
                  return (
                    <label
                      key={question.id}
                      className="flex cursor-pointer items-start gap-3 border-b border-gray-100 px-3 py-3 last:border-b-0 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleQuestion(question.id)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 accent-primary-500"
                      />
                      <div className="min-w-0">
                        <div className="mb-1 flex items-center gap-2">
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                            {question.indexLabel}
                          </span>
                          <span className="text-xs font-medium text-gray-500">
                            {question.sectionName}
                          </span>
                        </div>
                        <p className="text-sm text-gray-800">{question.text}</p>
                      </div>
                    </label>
                  );
                })
              ) : (
                <div className="px-3 py-4 text-sm text-gray-500">No matching questions found.</div>
              )}
            </div>
          </div>
        ) : null}

        <div className="mb-2">
          <label htmlFor="assign-user" className="mb-2 block text-sm font-semibold text-gray-800">
            Assign To
          </label>
          <select
            id="assign-user"
            value={assignedUserId}
            onChange={(event) => setAssignedUserId(event.target.value)}
            disabled={users.length === 0}
            className="w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none transition-all focus:border-primary-500 focus:shadow-[0_0_0_3px_rgba(0,156,229,0.1)]"
          >
            <option value="">
              {users.length === 0 ? "No users available" : "Select a user..."}
            </option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>
        {validationError || errorMessage ? (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {validationError ?? errorMessage}
          </div>
        ) : null}
      </div>

      <div className="flex gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="flex-1 rounded border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleAssign()}
          disabled={isSubmitting}
          className="flex-1 rounded bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting ? "Assigning..." : "Assign"}
        </button>
      </div>
    </Drawer>
  );
};
